import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

/**
 * ToastContext — global ephemeral notifications.
 *
 * Usage from any component:
 *   import { useToast } from '../context/ToastContext';
 *   const { toast } = useToast();
 *   toast.success('Saved!');
 *   toast.error('Could not save', { description: err.message });
 *   toast.info('Processing payroll…');
 *   toast.warning('Already paid for this month');
 *
 * The provider is mounted once at the App root; ToastContainer (rendered
 * inside the provider) handles positioning and stacking.
 */

const ToastContext = createContext({ toast: () => {}, dismiss: () => {} });

let nextId = 1;

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const push = useCallback((tone, title, opts = {}) => {
    const id = nextId++;
    const item = {
      id,
      tone,
      title,
      description: opts.description,
      duration: opts.duration ?? (tone === 'error' ? 6000 : 4000),
      action: opts.action, // { label, onClick }
    };
    setItems((prev) => [...prev, item]);
    if (item.duration > 0) {
      const timer = setTimeout(() => dismiss(id), item.duration);
      timersRef.current.set(id, timer);
    }
    return id;
  }, [dismiss]);

  // Convenience API: toast.success(...), toast.error(...), etc.
  const toast = useCallback((title, opts) => push('info', title, opts), [push]);
  toast.success = (title, opts) => push('success', title, opts);
  toast.error   = (title, opts) => push('error',   title, opts);
  toast.info    = (title, opts) => push('info',    title, opts);
  toast.warning = (title, opts) => push('warning', title, opts);

  return (
    <ToastContext.Provider value={{ toast, dismiss, items }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

export default ToastContext;
